import React from 'react';
import {
  Badge,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import type {
  ModelApprovalRecord,
  ModelApprovalReviewPayload,
  ModelApprovalScope,
} from '../../types/trapi';

const useStyles = makeStyles({
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  readOnlyGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: tokens.spacingVerticalXS,
    alignItems: 'start',
    '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
  },
  label: { color: tokens.colorNeutralForeground3 },
  hint: { color: tokens.colorNeutralForeground3 },
  confirmGrid: { display: 'grid', gap: tokens.spacingVerticalSNudge },
  errorText: { color: tokens.colorPaletteRedForeground1 },
  focusableButton: {
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusMedium,
    },
  },
});

export interface ModelApprovalReviewFormProps {
  record: ModelApprovalRecord;
  /**
   * Canonical apiPath inventory (F3, PR6 review) -- sourced from the same
   * ApisService.getApis() the Admin APIs page uses, so a scoped approval can
   * only ever reference an apiPath that actually exists in this environment,
   * never free-typed text that might contain a typo or an alias that was
   * never resolved to its canonical parent (PR5 is expected to resolve
   * aliases to their canonical parent at enforcement time; this form must
   * not let an admin approve against an unresolved alias string in the
   * first place).
   */
  apiPathOptions: string[];
  isLoading?: boolean;
  errorMessage?: string;
  onSubmit: (payload: ModelApprovalReviewPayload) => void;
  onCancel: () => void;
  formId?: string;
  hideActions?: boolean;
}

// Not-a-state sentinel — represents "leave the current state alone" (a pure
// field correction). Never sent to the backend.
const NO_STATE_CHANGE = '';
type StateOption = typeof NO_STATE_CHANGE | 'under_review' | 'approved' | 'rejected';
const NO_SCOPE = '';
type ScopeOption = typeof NO_SCOPE | ModelApprovalScope;

function normalizeOptionalText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// F3 (PR6 review): canonical multi-select checkbox list for approvedApiPaths,
// modeled on ResourcePoolForm's BackendsCheckboxSection. Options come from
// the live apiPath inventory; any path already on the record but no longer
// present in that inventory (e.g. a retired/renamed apiPath) is still shown
// and toggleable -- flagged distinctly -- so editing an existing scoped
// approval can never silently drop a legitimate value the reviewer didn't
// touch, and a stale value can only be removed by an explicit, visible
// action.
function ApprovedApiPathsCheckboxSection({
  selected,
  apiPathOptions,
  error,
  onChange,
}: {
  selected: string[];
  apiPathOptions: string[];
  error?: string;
  onChange: (paths: string[]) => void;
}) {
  const styles = useStyles();
  const [open, setOpen] = React.useState(true);
  const [filter, setFilter] = React.useState('');
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const staleSelected = React.useMemo(
    () => selected.filter((p) => !apiPathOptions.includes(p)),
    [selected, apiPathOptions],
  );

  const allOptions = React.useMemo(() => {
    const combined = new Set([...apiPathOptions, ...staleSelected]);
    return Array.from(combined).sort();
  }, [apiPathOptions, staleSelected]);

  const filtered = React.useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return allOptions;
    return allOptions.filter((p) => p.toLowerCase().includes(q));
  }, [allOptions, filter]);

  const toggle = (path: string) => {
    const next = new Set(selectedSet);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    onChange(Array.from(next));
  };

  return (
    <section style={{ display: 'grid', gap: 4 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none', outline: 'none' }}
        className={styles.focusableButton}
        onClick={() => setOpen((p) => !p)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((p) => !p); }}
      >
        {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        <Text weight="semibold">Approved API paths</Text>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
          {selected.length} selected
        </Caption1>
      </div>
      {error && <Caption1 className={styles.errorText}>{error}</Caption1>}
      {open && (
        <>
          <Input
            size="small"
            placeholder="Filter API paths…"
            value={filter}
            onChange={(_, d) => setFilter(d.value)}
          />
          <div style={{
            maxHeight: 220, overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke3}`,
            borderRadius: tokens.borderRadiusMedium, padding: 4,
          }}>
            {allOptions.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No API paths available</Caption1>
            ) : filtered.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No API paths match filter</Caption1>
            ) : (
              filtered.map((path) => (
                <Checkbox
                  key={path}
                  checked={selectedSet.has(path)}
                  onChange={() => toggle(path)}
                  label={
                    staleSelected.includes(path)
                      ? `${path} (not in current API inventory)`
                      : path
                  }
                  style={{ display: 'flex', width: '100%' }}
                />
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}

const ModelApprovalReviewForm: React.FC<ModelApprovalReviewFormProps> = ({
  record,
  apiPathOptions,
  isLoading,
  errorMessage,
  onSubmit,
  onCancel,
  formId,
  hideActions = false,
}) => {
  const styles = useStyles();

  const [modelName, setModelName] = React.useState(record.modelName || '');
  // Preserve unknown provider as blank — never guess a value for the user.
  const [modelProvider, setModelProvider] = React.useState(record.modelProvider || '');
  const [notes, setNotes] = React.useState(record.notes || '');
  const [intendedState, setIntendedState] = React.useState<StateOption>(NO_STATE_CHANGE);
  const [scope, setScope] = React.useState<ScopeOption>((record.scope as ScopeOption) || NO_SCOPE);
  const [approvedApiPaths, setApprovedApiPaths] = React.useState<string[]>(record.approvedApiPaths || []);
  const [errors, setErrors] = React.useState<{ modelName?: string; scope?: string; approvedApiPaths?: string; notes?: string }>({});
  const [pendingPayload, setPendingPayload] = React.useState<ModelApprovalReviewPayload | null>(null);
  const [pendingKind, setPendingKind] = React.useState<'approved' | 'rejected' | 'under_review' | 'modelNameChange' | null>(null);
  const [noOpMessage, setNoOpMessage] = React.useState<string | null>(null);

  // Reset the form whenever a different record is opened for review.
  React.useEffect(() => {
    setModelName(record.modelName || '');
    setModelProvider(record.modelProvider || '');
    setNotes(record.notes || '');
    setIntendedState(NO_STATE_CHANGE);
    setScope((record.scope as ScopeOption) || NO_SCOPE);
    setApprovedApiPaths(record.approvedApiPaths || []);
    setErrors({});
    setPendingPayload(null);
    setPendingKind(null);
    setNoOpMessage(null);
  }, [record]);

  const isCurrentlyApproved = record.state === 'approved';

  const buildPayload = (): ModelApprovalReviewPayload | null => {
    const e: { modelName?: string; scope?: string; approvedApiPaths?: string; notes?: string } = {};
    const trimmedName = modelName.trim();
    if (!trimmedName) e.modelName = 'Model name is required';

    const normalizedNotes = normalizeOptionalText(notes);
    // F6 (PR6 review): mirror the server's public-notes length bound
    // client-side so a reviewer gets immediate feedback instead of a round
    // trip 400.
    if (normalizedNotes && normalizedNotes.length > 1000) {
      e.notes = `Notes must be 1000 characters or fewer (currently ${normalizedNotes.length}) — this field is public via /models once the model is approved.`;
    }

    if (intendedState === 'approved') {
      if (scope !== 'global' && scope !== 'scoped') {
        e.scope = 'Scope is required to approve — never inherited from the stored record';
      } else if (scope === 'scoped' && approvedApiPaths.length === 0) {
        e.approvedApiPaths = 'At least one API path is required for a scoped approval';
      }
    }

    setErrors(e);
    if (Object.keys(e).length > 0) return null;

    const payload: ModelApprovalReviewPayload = {};

    if (trimmedName !== (record.modelName || '').trim()) {
      payload.modelName = trimmedName;
    }
    const normalizedProvider = normalizeOptionalText(modelProvider);
    const originalProvider = normalizeOptionalText(record.modelProvider || '');
    if (normalizedProvider !== originalProvider) {
      payload.modelProvider = normalizedProvider;
    }
    const originalNotes = normalizeOptionalText(record.notes || '');
    if (normalizedNotes !== originalNotes) {
      payload.notes = normalizedNotes;
    }

    if (intendedState !== NO_STATE_CHANGE) {
      payload.state = intendedState;
      if (intendedState === 'approved') {
        payload.scope = scope as ModelApprovalScope;
        if (scope === 'scoped') {
          payload.approvedApiPaths = approvedApiPaths;
        }
      }
    }

    return payload;
  };

  const noChanges = (payload: ModelApprovalReviewPayload) => Object.keys(payload).length === 0;

  // F16 (PR6 review): compute whether the form is currently pristine
  // (no field differs from the stored record and no new state is
  // selected) so the Save button can be disabled proactively, in
  // addition to the noOpMessage shown if a no-op submit is attempted
  // anyway (e.g. via keyboard Enter). This intentionally mirrors
  // buildPayload's diff logic but has no side effects (no setErrors),
  // so it is safe to evaluate on every render.
  const isPristine = React.useMemo(() => {
    if (intendedState !== NO_STATE_CHANGE) return false;
    const trimmedName = modelName.trim();
    if (trimmedName !== (record.modelName || '').trim()) return false;
    const normalizedProvider = normalizeOptionalText(modelProvider);
    const originalProvider = normalizeOptionalText(record.modelProvider || '');
    if (normalizedProvider !== originalProvider) return false;
    const normalizedNotes = normalizeOptionalText(notes);
    const originalNotes = normalizeOptionalText(record.notes || '');
    if (normalizedNotes !== originalNotes) return false;
    return true;
  }, [intendedState, modelName, modelProvider, notes, record.modelName, record.modelProvider, record.notes]);

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setNoOpMessage(null);
    const payload = buildPayload();
    if (!payload) return;
    if (noChanges(payload)) {
      // F16 (PR6 review): a silent no-op submit gives no feedback at all.
      setNoOpMessage('No changes to save — adjust a field or select a new state first.');
      return;
    }

    // F4 (PR6 review): confirmation is required not only for approve/reject,
    // but also for:
    //   - an approved record transitioning to under_review (this
    //     immediately unpublishes it -- the RUNTIMECI mirror is deleted),
    //     the same real-world impact as a reject;
    //   - a modelName change on a currently-approved record, since the
    //     mirror is keyed by this document's id but re-upserted with the
    //     NEW modelName -- silently re-pointing which model an existing
    //     approval decision applies to is exactly the kind of change that
    //     should require the reviewer to pause and confirm.
    // A pure field correction that does not touch modelName, and a
    // detected/rejected/under_review record transitioning to under_review,
    // save directly -- their impact is not a live-serving change.
    if (payload.state === 'approved') {
      setPendingKind('approved');
      setPendingPayload(payload);
      return;
    }
    if (payload.state === 'rejected') {
      setPendingKind('rejected');
      setPendingPayload(payload);
      return;
    }
    if (payload.state === 'under_review' && isCurrentlyApproved) {
      setPendingKind('under_review');
      setPendingPayload(payload);
      return;
    }
    if (
      payload.modelName !== undefined &&
      isCurrentlyApproved &&
      !payload.state
    ) {
      setPendingKind('modelNameChange');
      setPendingPayload(payload);
      return;
    }
    onSubmit(payload);
  };

  const confirmTitle = React.useMemo(() => {
    switch (pendingKind) {
      case 'approved': return 'Confirm Approval';
      case 'rejected': return 'Confirm Rejection';
      case 'under_review': return 'Confirm Unpublish (Under Review)';
      case 'modelNameChange': return 'Confirm Model Name Change';
      default: return 'Confirm';
    }
  }, [pendingKind]);

  const confirmSummary = React.useMemo(() => {
    if (!pendingPayload) return null;
    const name = pendingPayload.modelName || record.modelName;
    if (pendingKind === 'approved') {
      return pendingPayload.scope === 'scoped'
        ? `Approve "${name}", scoped to: ${(pendingPayload.approvedApiPaths || []).join(', ')}`
        : `Approve "${name}" globally — it becomes servable on every API path`;
    }
    if (pendingKind === 'rejected') {
      return `Reject "${name}" — this immediately removes it from the runtime mirror`;
    }
    if (pendingKind === 'under_review') {
      const scopeDescription = record.scope === 'scoped'
        ? `scoped to: ${(record.approvedApiPaths || []).join(', ')}`
        : 'globally';
      return `"${name}" is currently APPROVED (${scopeDescription}) and being served. Moving it to Under Review deletes the runtime mirror immediately — it stops being servable and disappears from /models right away, before any further review decision is made.`;
    }
    if (pendingKind === 'modelNameChange') {
      const scopeDescription = record.scope === 'scoped'
        ? `scoped to: ${(record.approvedApiPaths || []).join(', ')}`
        : 'globally';
      return `This record is currently APPROVED (${scopeDescription}) for model name "${record.modelName}". Changing it to "${name}" re-points this same approval decision (scope, approvedApiPaths, and the runtime mirror) onto the new model name — it does not create a separate record. Confirm this is a correction to the existing model's recorded name, not an attempt to approve a different model.`;
    }
    return null;
  }, [pendingPayload, pendingKind, record.modelName, record.scope, record.approvedApiPaths]);

  const hasApprovedScopeChangeAffordanceNote = isCurrentlyApproved && intendedState !== 'approved';

  return (
    <>
      <form id={formId} onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
        {errorMessage && (
          <Text className={styles.errorText}>{errorMessage}</Text>
        )}
        {noOpMessage && (
          <Text className={styles.hint}>{noOpMessage}</Text>
        )}

        <section className={styles.readOnlyGrid}>
          <Text className={styles.label}>Detected</Text>
          <Text>{record.firstDetectedAt ? new Date(record.firstDetectedAt).toLocaleString() : '-'}</Text>
          <Text className={styles.label}>Detected on</Text>
          <Text>{record.firstDetectedOnEndpoint || '-'}</Text>
          <Text className={styles.label}>Current state</Text>
          <Text>{record.state || 'detected'}</Text>
          {record.reviewedBy && (
            <>
              <Text className={styles.label}>Last reviewed by (object ID)</Text>
              <Text>
                {record.reviewedBy}
                {record.reviewedAt ? ` — ${new Date(record.reviewedAt).toLocaleString()}` : ''}
              </Text>
            </>
          )}
        </section>

        <section style={{ display: 'grid', gap: 12 }}>
          <Text weight="semibold">Field corrections</Text>
          <Field label="Model Name" required validationMessage={errors.modelName}>
            <Input value={modelName} onChange={(_, d) => setModelName(d.value)} />
          </Field>
          <Field
            label="Provider"
            hint="Left blank when automation could not determine one — never guess a value here."
          >
            <Input
              value={modelProvider}
              onChange={(_, d) => setModelProvider(d.value)}
              placeholder="e.g. OpenAI"
            />
          </Field>
          <Field
            label="Notes"
            validationMessage={errors.notes}
            hint="Visible to every caller of GET /models once this record is approved — never put internal-only content here. Limited to 1000 characters."
          >
            <Textarea value={notes} onChange={(_, d) => setNotes(d.value)} rows={3} />
          </Field>
        </section>

        <section style={{ display: 'grid', gap: 12, paddingTop: 4, borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
          <Text weight="semibold">State transition</Text>
          <Field label="New state" hint="Leave as 'No change' to save only the field corrections above.">
            <Dropdown
              value={
                intendedState === NO_STATE_CHANGE
                  ? 'No change'
                  : intendedState === 'under_review'
                    ? 'Under review'
                    : intendedState === 'approved'
                      ? 'Approve'
                      : 'Reject'
              }
              selectedOptions={[intendedState]}
              onOptionSelect={(_, d) => {
                if (d.optionValue !== undefined) setIntendedState(d.optionValue as StateOption);
              }}
            >
              <Option value={NO_STATE_CHANGE}>No change</Option>
              <Option value="under_review">Under review</Option>
              <Option value="approved">Approve</Option>
              <Option value="rejected">Reject</Option>
            </Dropdown>
          </Field>

          {hasApprovedScopeChangeAffordanceNote && (
            <Caption1 className={styles.hint}>
              This record is currently <Badge appearance="tint" color="success" size="small">approved</Badge>{' '}
              ({record.scope === 'scoped' ? `scoped to ${(record.approvedApiPaths || []).join(', ')}` : 'global'}).
              To change its scope or approved API paths, select <strong>Approve</strong> above and provide the
              updated scope/paths — this re-confirms the approval decision under your identity. Selecting a
              different state (or none) here leaves scope/approvedApiPaths untouched.
            </Caption1>
          )}

          {intendedState === 'approved' && (
            <>
              <Field label="Scope" required validationMessage={errors.scope}>
                <Dropdown
                  value={scope === 'global' ? 'Global' : scope === 'scoped' ? 'Scoped' : ''}
                  selectedOptions={scope ? [scope] : []}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue !== undefined) setScope(d.optionValue as ScopeOption);
                  }}
                >
                  <Option value="global">Global</Option>
                  <Option value="scoped">Scoped</Option>
                </Dropdown>
              </Field>
              {scope === 'scoped' && (
                <ApprovedApiPathsCheckboxSection
                  selected={approvedApiPaths}
                  apiPathOptions={apiPathOptions}
                  error={errors.approvedApiPaths}
                  onChange={setApprovedApiPaths}
                />
              )}
            </>
          )}
        </section>

        {!hideActions && (
          <div className={styles.formActions}>
            <Button appearance="outline" type="button" onClick={onCancel} disabled={!!isLoading}>
              Cancel
            </Button>
            <Button appearance="primary" type="submit" disabled={!!isLoading || isPristine}>
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </form>

      <Dialog
        open={!!pendingPayload}
        onOpenChange={(_, d) => {
          // F5 (PR6 review): only the explicit Cancel/dismiss path clears the
          // pending confirmation -- a failed save must NOT auto-close this
          // dialog, since the error is rendered inside it (below) and would
          // become invisible (hidden behind this same modal) if the dialog
          // closed and the error surfaced only in the parent flyout instead.
          if (!d.open && !isLoading) setPendingPayload(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{confirmTitle}</DialogTitle>
            <div className={styles.confirmGrid}>
              <Text>{confirmSummary}</Text>
              <Text className={styles.hint}>This action is recorded with your identity and a timestamp.</Text>
              {errorMessage && (
                <Text className={styles.errorText}>{errorMessage}</Text>
              )}
            </div>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingPayload(null)} disabled={!!isLoading}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={!!isLoading}
                onClick={() => {
                  if (pendingPayload) onSubmit(pendingPayload);
                }}
              >
                {isLoading ? 'Saving…' : errorMessage ? 'Retry' : 'Confirm'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ModelApprovalReviewForm;

